import { Heart, Pause, Volume2, VolumeX, Music, Settings, Timer } from "lucide-react";
import { useLang } from "@/i18n";
import { formatTime } from "@/lib/format";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/game-assets`;

export const Hud = ({ hud, character, charColor, muted, onMute, onPause, onMusic, onSettings, onTouch, itemEmoji = "🍺" }) => {
  const { t } = useLang();
  const buzz = (ms) => { try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) { /* unsupported */ } };
  const NOSEL = { touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" };
  const press = (name) => ({
    // Register input FIRST so haptics never delay/alter control responsiveness or game speed.
    onTouchStart: (e) => { e.preventDefault(); onTouch(name, true); buzz(name === "left" || name === "right" ? 8 : 16); },
    onTouchEnd: (e) => { e.preventDefault(); onTouch(name, false); },
    onTouchCancel: (e) => { e.preventDefault(); onTouch(name, false); },
    onContextMenu: (e) => e.preventDefault(),
    onMouseDown: () => onTouch(name, true),
    onMouseUp: () => onTouch(name, false),
    onMouseLeave: () => onTouch(name, false),
    style: NOSEL,
  });

  return (
    <>
      {/* Top HUD */}
      <div className="absolute top-0 left-0 w-full p-3 md:p-4 safe-x safe-t flex justify-between items-start z-40 pointer-events-none font-pixel pixel-stroke text-white">
        <div className="flex flex-col gap-2">
          <div data-testid="hud-score" className="text-xs md:text-base">{String(hud.score).padStart(6, "0")}</div>
          <div data-testid="hud-coins" className="text-[10px] md:text-sm text-[#FFEB3B] flex items-center gap-1">{itemEmoji} x {hud.beers}</div>
          <div data-testid="hud-timer" className="text-[10px] md:text-sm text-white/90 flex items-center gap-1"><Timer size={12} strokeWidth={2.5} /> {formatTime(hud.timeMs)}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <div className="text-[10px] md:text-xs text-[#FFEB3B]">{hud.short}</div>
          <div className="text-[8px] md:text-[11px] text-white/90 max-w-[40vw] truncate text-center">{hud.levelName}</div>
          <div className="mt-1 w-32 md:w-44 h-3 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-150" style={{ width: `${hud.fiesta}%`, background: hud.powered ? "#FFEB3B" : "#E91E63" }} />
          </div>
          <div className={`text-[8px] md:text-[10px] ${hud.powered ? "text-[#FFEB3B] blink" : "text-white/70"}`}>{hud.powered ? t("fiestaOn") : t("fiesta")}</div>

          {hud.boss && (
            <div className="mt-2 flex flex-col items-center" data-testid="boss-hp">
              <div className="text-[8px] md:text-[10px] text-[#ff5252] blink">{t("jefa")}</div>
              <div className="w-40 md:w-56 h-3 rounded-full bg-black/40 overflow-hidden mt-1">
                <div className="h-full rounded-full bg-[#ff3030] transition-all duration-150" style={{ width: `${(hud.boss.hp / hud.boss.max) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 pointer-events-auto">
          <div data-testid="hud-lives" className="flex gap-1">
            {Array.from({ length: hud.maxLives || 3 }).map((_, i) => (
              <Heart key={i} size={18} strokeWidth={3} className={i < hud.lives ? "text-[#FF5252]" : "text-white/25"} fill={i < hud.lives ? "#FF5252" : "none"} />
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="hud-music-btn" onClick={onMusic} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Music size={16} strokeWidth={2.5} />
            </button>
            <button data-testid="hud-mute-btn" onClick={onMute} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              {muted ? <VolumeX size={16} strokeWidth={2.5} /> : <Volume2 size={16} strokeWidth={2.5} />}
            </button>
            <button data-testid="hud-pause-btn" onClick={onPause} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Pause size={16} strokeWidth={2.5} />
            </button>
            <button data-testid="hud-settings-btn" onClick={onSettings} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Settings size={16} strokeWidth={2.5} />
            </button>
          </div>

          {/* Current player card — under the settings buttons, so users know which character they're using */}
          {character && (
            <div className="flex flex-col items-center gap-1 mt-1" data-testid="hud-player-card">
              <div
                className="pixel-card relative overflow-hidden"
                style={{ width: "44px", aspectRatio: "3 / 4", background: `linear-gradient(160deg, ${character.colors.accent}, #111)` }}
              >
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 5px,transparent 5px 12px)" }} />
                <img
                  src={`${ASSET_BASE}/${character.spriteId || (character.variants && character.variants[0].sprite) || character.id}.webp`}
                  alt={character.name}
                  draggable={false}
                  style={{ height: "92%", width: "94%", objectFit: "cover", objectPosition: "center top", filter: "drop-shadow(1px 2px 2px rgba(0,0,0,0.5))" }}
                />
              </div>
              <span className="font-pixel pixel-stroke text-white text-[7px] md:text-[9px] max-w-[72px] truncate text-center leading-tight">{character.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Touch controls (mobile) */}
      <div className="touch-only absolute bottom-5 left-0 w-full px-5 safe-x safe-b flex justify-between items-end z-40 select-none touch-none">
        <div className="flex gap-4 translate-y-3">
          <button {...press("left")} className="pixel-btn bg-black/60 text-white w-20 h-20 text-3xl opacity-90 select-none touch-none" data-testid="touch-left">◄</button>
          <button {...press("right")} className="pixel-btn bg-black/60 text-white w-20 h-20 text-3xl opacity-90 select-none touch-none" data-testid="touch-right">►</button>
        </div>
        <div className="flex gap-4">
          <button {...press("dance")} className="pixel-btn bg-[#E91E63] text-white w-20 h-20 text-base opacity-90 select-none touch-none" data-testid="touch-dance">♪</button>
          <button {...press("action")} className="pixel-btn bg-[#0067C6] text-white w-20 h-20 text-sm opacity-90 select-none touch-none" data-testid="touch-action" style={{ borderColor: charColor, ...NOSEL }}>PWR</button>
          <button {...press("jump")} className="pixel-btn bg-[#FF5722] text-white w-20 h-20 text-2xl opacity-90 select-none touch-none" data-testid="touch-jump">▲</button>
        </div>
      </div>
    </>
  );
};
